import { useState } from "react";
import { t, font, Button, Panel, Badge } from "@realtyflows/ui";
import { aiSummarize, aiScoreClient, aiDraftReplies, aiFollowUpPlan, aiDetectStage, aiAsk } from "../api";

const AI_TABS = [
  { key: "summarize", label: "Summarize", icon: "\u{1F9E0}" },
  { key: "score", label: "Score Client", icon: "\u{1F3AF}" },
  { key: "replies", label: "Draft Replies", icon: "\u270D\uFE0F" },
  { key: "followup", label: "Follow-Up Plan", icon: "\u{1F4C5}" },
  { key: "stage", label: "Detect Stage", icon: "\u{1F3E0}" },
  { key: "ask", label: "Ask AI", icon: "\u{1F4A1}" },
];

export default function AIPanel({ clientId, clientName, onStageApply, onDataChange }) {
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [question, setQuestion] = useState("");
  const [copied, setCopied] = useState(null);

  const run = async (tab) => {
    setActiveTab(tab);
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      let data;
      if (tab === "summarize") data = await aiSummarize(clientId);
      else if (tab === "score") { data = await aiScoreClient(clientId); if (onDataChange) onDataChange(); }
      else if (tab === "replies") data = await aiDraftReplies(clientId);
      else if (tab === "followup") data = await aiFollowUpPlan(clientId);
      else if (tab === "stage") data = await aiDetectStage(clientId);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runAsk = async () => {
    if (!question.trim()) return;
    setActiveTab("ask");
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      const data = await aiAsk(clientId, question);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const urgencyColor = (u) => u === "High" ? t.coral : u === "Medium" ? t.amber : t.emerald;

  return (
    <Panel padding={0} style={{ overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${t.navy}, ${t.navyLight})`,
        padding: "16px 20px", display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: "50%", background: t.goldLight,
          animation: "pulse 2s infinite", flexShrink: 0,
        }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: t.goldLight, fontFamily: font.body, letterSpacing: "0.5px" }}>
          AI ASSISTANT
        </span>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontFamily: font.mono, marginLeft: "auto" }}>
          for {clientName}
        </span>
      </div>

      {/* Tab Buttons */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, padding: "14px 16px", borderBottom: `1.5px solid ${t.border}` }}>
        {AI_TABS.map(tab => (
          <div
            key={tab.key}
            onClick={() => tab.key === "ask" ? setActiveTab("ask") : run(tab.key)}
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "8px 10px",
              borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
              background: activeTab === tab.key ? t.emeraldLight : t.surfaceWarm,
              border: `1.5px solid ${activeTab === tab.key ? t.emerald : t.border}`,
              fontSize: 11, fontWeight: 600, color: activeTab === tab.key ? t.emerald : t.textSecondary,
              fontFamily: font.body,
            }}
          >
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            {tab.label}
          </div>
        ))}
      </div>

      {/* Content Area */}
      <div style={{ padding: "16px 20px", minHeight: 120 }}>
        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 12 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 8, height: 8, borderRadius: "50%", background: t.emerald,
                  animation: `pulseDot 1.2s ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>Analyzing...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{ background: t.coralLight, border: `1px solid ${t.coral}30`, borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 12, color: t.coral, fontFamily: font.body }}>{error}</div>
          </div>
        )}

        {/* No Selection */}
        {!activeTab && !loading && (
          <div style={{ textAlign: "center", padding: "24px 0", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>
            Select a feature above to get AI insights for this client.
          </div>
        )}

        {/* ─── SUMMARIZE RESULT ─── */}
        {activeTab === "summarize" && result && !loading && (
          <div style={{ animation: "fadeSlide 0.3s ease" }}>
            <div style={{ marginBottom: 16 }}>
              {result.brief?.map((line, i) => (
                <div key={i} style={{ fontSize: 13, color: t.textPrimary, lineHeight: 1.6, fontFamily: font.body, marginBottom: 4 }}>
                  {line}
                </div>
              ))}
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono, marginBottom: 4 }}>KEY ASK</div>
              <div style={{ fontSize: 13, color: t.textPrimary, fontWeight: 600, fontFamily: font.body }}>{result.key_ask}</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Badge variant={result.urgency === "High" ? "coral" : result.urgency === "Medium" ? "amber" : "emerald"}>
                {result.urgency} Urgency
              </Badge>
              {result.action_tags?.map(tag => (
                <span key={tag} style={{
                  padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: t.emeraldLight, color: t.emerald, fontFamily: font.body,
                }}>{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* ─── SCORE RESULT ─── */}
        {activeTab === "score" && result && !loading && (
          <div style={{ animation: "fadeSlide 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: `conic-gradient(${result.score >= 70 ? t.emerald : result.score >= 45 ? t.amber : t.coral} ${result.score * 3.6}deg, ${t.surfaceWarm} 0deg)`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div style={{
                  width: 50, height: 50, borderRadius: "50%", background: t.surface,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.mono,
                }}>{result.score}</div>
              </div>
              <div>
                <Badge variant={result.sentiment?.includes("Positive") ? "emerald" : result.sentiment === "Neutral" ? "amber" : "default"}>
                  {result.sentiment}
                </Badge>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
                  Churn risk: <span style={{ color: urgencyColor(result.churn_risk), fontWeight: 700 }}>{result.churn_risk}</span>
                </div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, fontWeight: 700 }}>BUDGET</div>
                <div style={{ fontSize: 13, color: t.textPrimary, fontWeight: 600, fontFamily: font.body, marginTop: 2 }}>{result.budget_extracted}</div>
              </div>
              <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, fontWeight: 700 }}>TIMELINE</div>
                <div style={{ fontSize: 13, color: t.textPrimary, fontWeight: 600, fontFamily: font.body, marginTop: 2 }}>{result.timeline_extracted}</div>
              </div>
            </div>
            {result.churn_reason && (
              <div style={{ background: t.coralLight, borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, color: t.coral, fontFamily: font.body }}>
                {result.churn_reason}
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono, marginBottom: 6 }}>SIGNALS</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {result.signals?.map(s => (
                  <span key={s} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: t.emeraldLight, color: t.emerald, fontFamily: font.body }}>{s}</span>
                ))}
              </div>
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12, fontSize: 13, color: t.textPrimary, lineHeight: 1.5, fontFamily: font.body }}>
              {result.recommendation}
            </div>
          </div>
        )}

        {/* ─── DRAFT REPLIES RESULT ─── */}
        {activeTab === "replies" && result && !loading && (
          <div style={{ animation: "fadeSlide 0.3s ease" }}>
            {["professional", "warm", "urgent"].map(tone => {
              const r = result[tone];
              if (!r) return null;
              const label = tone.charAt(0).toUpperCase() + tone.slice(1);
              const toneColor = tone === "professional" ? t.sky : tone === "warm" ? t.emerald : t.coral;
              return (
                <div key={tone} style={{
                  background: t.surfaceWarm, borderRadius: 10, padding: 14, marginBottom: 10,
                  border: `1.5px solid ${toneColor}20`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <Badge variant={tone === "professional" ? "sky" : tone === "warm" ? "emerald" : "coral"}>
                      {label}
                    </Badge>
                    <div
                      onClick={() => copyText(`Subject: ${r.subject}\n\n${r.body}`, tone)}
                      style={{
                        fontSize: 11, color: copied === tone ? t.emerald : t.textMuted,
                        cursor: "pointer", fontWeight: 600, fontFamily: font.body,
                      }}
                    >{copied === tone ? "Copied!" : "Copy"}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.body }}>
                    {r.subject}
                  </div>
                  <div style={{ fontSize: 12, color: t.textSecondary, lineHeight: 1.6, fontFamily: font.body, whiteSpace: "pre-line" }}>
                    {r.body}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ─── FOLLOW-UP PLAN RESULT ─── */}
        {activeTab === "followup" && result && !loading && (
          <div style={{ animation: "fadeSlide 0.3s ease" }}>
            {result.steps?.map((step, i) => (
              <div key={i} style={{
                display: "flex", gap: 14, marginBottom: 14, paddingBottom: 14,
                borderBottom: i < 2 ? `1px solid ${t.border}` : "none",
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                  background: `linear-gradient(135deg, ${t.navy}, ${t.navyLight})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 800, color: t.goldLight, fontFamily: font.mono,
                }}>D{step.day}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    <Badge variant={step.channel === "Email" ? "sky" : step.channel === "Call" ? "emerald" : "amber"}>
                      {step.channel}
                    </Badge>
                    <span style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{step.subject}</span>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {step.talking_points?.map((tp, j) => (
                      <li key={j} style={{ fontSize: 12, color: t.textSecondary, lineHeight: 1.6, fontFamily: font.body }}>{tp}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <div style={{ flex: 1, background: t.surfaceWarm, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>STRATEGY</div>
                <div style={{ fontSize: 12, color: t.textPrimary, marginTop: 2, fontFamily: font.body }}>{result.strategy_note}</div>
              </div>
              <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 10, minWidth: 120 }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono }}>BEST TIME</div>
                <div style={{ fontSize: 12, color: t.emerald, fontWeight: 600, marginTop: 2, fontFamily: font.body }}>{result.best_time}</div>
              </div>
            </div>
          </div>
        )}

        {/* ─── DETECT STAGE RESULT ─── */}
        {activeTab === "stage" && result && !loading && (
          <div style={{ animation: "fadeSlide 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, fontFamily: font.mono, marginBottom: 4 }}>CURRENT</div>
                <Badge variant="default">{result.current_stage}</Badge>
              </div>
              <div style={{ fontSize: 18, color: t.textMuted }}>&rarr;</div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, fontFamily: font.mono, marginBottom: 4 }}>SUGGESTED</div>
                <Badge variant={result.should_move ? "emerald" : "default"}>{result.suggested_stage}</Badge>
              </div>
              <div style={{ marginLeft: "auto" }}>
                <Badge variant={result.confidence === "High" ? "emerald" : result.confidence === "Medium" ? "amber" : "default"}>
                  {result.confidence} Confidence
                </Badge>
              </div>
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: t.textPrimary, lineHeight: 1.6, fontFamily: font.body }}>{result.reasoning}</div>
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono, marginBottom: 4 }}>NEXT ACTION</div>
              <div style={{ fontSize: 12, color: t.textPrimary, fontFamily: font.body }}>{result.next_action}</div>
            </div>
            {result.should_move && onStageApply && (
              <Button variant="primary" size="sm" onClick={async () => { await onStageApply(result.suggested_stage); if (onDataChange) onDataChange(); }}>
                Apply: Move to {result.suggested_stage}
              </Button>
            )}
          </div>
        )}

        {/* ─── ASK AI ─── */}
        {activeTab === "ask" && !loading && (
          <div style={{ animation: "fadeSlide 0.3s ease" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <input
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => e.key === "Enter" && runAsk()}
                placeholder="Ask anything about this client..."
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 8, fontSize: 13,
                  border: `1.5px solid ${t.border}`, background: t.surfaceWarm,
                  color: t.textPrimary, outline: "none", fontFamily: font.body,
                }}
              />
              <Button variant="primary" size="sm" onClick={runAsk}>Ask</Button>
            </div>
            {result && (
              <div>
                <div style={{
                  background: t.surfaceWarm, borderRadius: 10, padding: 14, marginBottom: 10,
                  fontSize: 13, color: t.textPrimary, lineHeight: 1.7, fontFamily: font.body, whiteSpace: "pre-line",
                }}>
                  {result.answer}
                </div>
                {result.tips?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, fontFamily: font.mono, marginBottom: 6 }}>TIPS</div>
                    {result.tips.map((tip, i) => (
                      <div key={i} style={{
                        display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6,
                        fontSize: 12, color: t.textSecondary, fontFamily: font.body,
                      }}>
                        <span style={{ color: t.emerald, fontWeight: 700, flexShrink: 0 }}>&bull;</span>
                        {tip}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!result && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["What's their budget?", "Negotiation tips", "Market trends", "Timeline info"].map(q => (
                  <div key={q} onClick={() => { setQuestion(q); }} style={{
                    padding: "6px 12px", borderRadius: 6, fontSize: 11, cursor: "pointer",
                    background: t.surfaceWarm, border: `1px solid ${t.border}`,
                    color: t.textSecondary, fontFamily: font.body,
                  }}>{q}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
